import Select from '../ui/Select';
import Input from '../ui/Input';
import { RESULT_OPTIONS } from '../../constants/AssetTestConfig';

/**
 * Generic (non-RCD/exit-emergency/workshop) test type, metrics, result, and
 * failure-reason fields for RecordTestModal. Extracted from
 * RecordTestModal.jsx (Brief 44 commit 8).
 *
 * Props:
 *   register — react-hook-form register fn.
 *   errors — react-hook-form formState.errors.
 *   testOptions — asset-type test type options (Test Type dropdown).
 *   showMetrics — whether the selected test type requires a metrics input.
 *   metricsLabel — label for the metrics input (e.g. "Trip Time (ms)").
 *   tripTimeStrictRequired — whether the metrics input is strictly required
 *     (selected failure reason demands a trip time).
 *   result — current watched result value ('pass' | 'fail' | 'needs_repair');
 *     the failure-reason dropdown only renders on 'fail'.
 *   failureOptions — asset-type failure reason options.
 */
export default function GenericTestFields({
  register,
  errors,
  testOptions,
  showMetrics,
  metricsLabel,
  tripTimeStrictRequired,
  result,
  failureOptions,
}) {
  return (
    <>
      {/* GENERIC TEST TYPE & METRICS */}
      <Select
        id="record-test-type"
        label="Test Type *"
        {...register('testType', { required: 'Test type is required' })}
      >
        {testOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>

      {showMetrics && (
        <Input
          id="record-test-trip-time"
          label={`${metricsLabel} *`}
          type="number"
          min={0}
          step={1}
          placeholder="e.g. 28"
          className={tripTimeStrictRequired ? 'form-group-required-highlight' : ''}
          error={errors.tripTimeMs?.message}
          {...register('tripTimeMs', {
            required: tripTimeStrictRequired
              ? 'Trip time (ms) is required when recording Trip Time Failure'
              : `${metricsLabel} is required for this test type`,
            min: { value: 0, message: 'Must be 0 or greater' },
          })}
        />
      )}

      {/* GENERIC RESULT DROPDOWN */}
      <Select id="record-test-result" label="Result *" {...register('result', { required: true })}>
        {RESULT_OPTIONS.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </Select>

      {/* GENERIC FAIL DETAILS */}
      {result === 'fail' && (
        <Select
          id="record-test-failure-reason"
          label="Reason for Failure"
          {...register('failureReason')}
        >
          <option value="">Select reason (optional)</option>
          {failureOptions.map((reason) => (
            <option key={reason} value={reason}>
              {reason}
            </option>
          ))}
        </Select>
      )}
    </>
  );
}
